import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Pagination extends i18nMixin(NeptuneElement) {
    static PAGE_SIZES = [5, 10, 25, 50, 100];

    #page = 1;
    #pageSize = 10;
    #pageSizeValue = '10';
    #fallbackTotalItems = 0;
    #changeDispatchTimeoutId = null;
    #changeDispatchDelayMs = 800;

    templateMarkup = `
    <nav id="pagination" aria-label="pagination">
      <ul>
        <li>
          <span>Items per page:</span>
          <neptune-select>
            <option value="10" selected>10</option>
          </neptune-select>
        </li>

        <li aria-live="polite">
          <span data-range-text></span>
        </li>

        <li>
            <button type="button" data-action="first" aria-label="First page" disabled aria-disabled="true">
                <i class="fa-solid fa-angles-left" aria-hidden="true"></i>
                <span class="visually-hidden-a11y">First page</span>
            </button>
            <button type="button" data-action="prev" aria-label="Previous page" disabled aria-disabled="true">
                <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
                <span class="visually-hidden-a11y">Previous page</span>
            </button>
            <button type="button" data-action="next" aria-label="Next page" disabled aria-disabled="true">
                <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                <span class="visually-hidden-a11y">Next page</span>
            </button>
            <button type="button" data-action="last" aria-label="Last page" disabled aria-disabled="true">
                <i class="fa-solid fa-angles-right" aria-hidden="true"></i>
                <span class="visually-hidden-a11y">Last page</span>
            </button>
        </li>
      </ul>
    </nav>
  `;

    componentCssUrl = '/src/components/navigation/pagination/pagination.css';

    constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    static get observedAttributes() {
        return ['total-items', 'culture'];
    }

    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
        if (culture) { i18n.setLocale(culture); }
        this.#applyTranslations();
        this.processInitialAttributesAfterRender(Pagination.observedAttributes);
        this.#updatePageSizeSelect();
        this.#updateRangeText();
        this.#updateLinksDisabledState();
    }

    async disconnectedCallback() {
        this.#clearQueuedPaginationChange();
        await super.disconnectedCallback();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered || oldValue === newValue) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name) {
        switch (name) {
            case 'culture': {
                const culture = this.getAttribute('culture');
                if (culture) { 
                    i18n.setLocale(culture);
                }
                break;
            }
            case 'total-items':
                this.#updatePageSizeSelect();
                this.#updateRangeText();
                this.#updateLinksDisabledState();
                break;
            default:
                break;
        }
    }

    /**
     * @description Handles locale changes by applying translations.
     */
    onLocaleChange() {
        this.#applyTranslations();
        this.#updateRangeText();
        this.#updatePageSizeSelect();
    }

    /**
     * @description Applies translations to pagination using available keys.
     */
    #applyTranslations() {
        const firstButton = this.shadowRoot?.querySelector('button[data-action="first"]');
        if (firstButton) {
            firstButton.setAttribute('aria-label', this.translate('PAGINATION.FIRST'));
            const span = firstButton.querySelector('.visually-hidden-a11y');
            if (span) { span.textContent = this.translate('PAGINATION.FIRST'); }
        }

        const prevButton = this.shadowRoot?.querySelector('button[data-action="prev"]');
        if (prevButton) {
            prevButton.setAttribute('aria-label', this.translate('PAGINATION.PREVIOUS'));
            const span = prevButton.querySelector('.visually-hidden-a11y');
            if (span) { span.textContent = this.translate('PAGINATION.PREVIOUS'); }
        }

        const nextButton = this.shadowRoot?.querySelector('button[data-action="next"]');
        if (nextButton) {
            nextButton.setAttribute('aria-label', this.translate('PAGINATION.NEXT'));
            const span = nextButton.querySelector('.visually-hidden-a11y');
            if (span) { span.textContent = this.translate('PAGINATION.NEXT'); }
        }

        const lastButton = this.shadowRoot?.querySelector('button[data-action="last"]');
        if (lastButton) {
            lastButton.setAttribute('aria-label', this.translate('PAGINATION.LAST'));
            const span = lastButton.querySelector('.visually-hidden-a11y');
            if (span) { span.textContent = this.translate('PAGINATION.LAST'); }
        }

        const itemsPerPageLabel = this.shadowRoot?.querySelector('li:first-of-type > span:not([data-range-text])');
        if (itemsPerPageLabel) {
            itemsPerPageLabel.textContent = this.translate('PAGINATION.ITEMS_PER_PAGE');
        }
    }

    setEventListeners() {
        this.shadowRoot.addEventListener('click', this.handleClick);
        this.shadowRoot.addEventListener('change', this.handleChange);
    }

    removeEventListeners() {
        this.shadowRoot.removeEventListener('click', this.handleClick);
        this.shadowRoot.removeEventListener('change', this.handleChange);
    }

    get totalItems() {
        if (!this.hasAttribute('total-items')) {
            return this.#fallbackTotalItems;
        }

        const value = Number(this.getAttribute('total-items') ?? 0);
        return Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
    }

    get totalPages() {
        if (!this.totalItems) {
            return 0;
        }
        return Math.max(1, Math.ceil(this.totalItems / this.#pageSize));
    }

    getVisiblePageSizeOptions() {
        const total = this.totalItems;

        if (total > 0 && total < 100) {
            return Pagination.PAGE_SIZES.filter((value) => value < total).concat('all');
        }

        return Pagination.PAGE_SIZES;
    }

    syncState({ page = 1, pageSize, pageSizeValue, totalItems } = {}) {
        const normalizedTotalItems = Number.isFinite(totalItems) && totalItems >= 0 ? Math.floor(totalItems) : 0;
        this.#fallbackTotalItems = normalizedTotalItems;

        const normalizedPageSizeValue = typeof pageSizeValue === 'string' ? pageSizeValue.trim().toLowerCase() : '';

        if (normalizedPageSizeValue === 'all') {
            this.#pageSizeValue = 'all';
            this.#pageSize = Math.max(1, this.totalItems);
        } else {
            const candidatePageSize = Number.isFinite(pageSize)
                ? Math.floor(pageSize)
                : Number(normalizedPageSizeValue);

            if (Number.isFinite(candidatePageSize) && candidatePageSize >= 1) {
                this.#pageSize = candidatePageSize;
                this.#pageSizeValue = String(candidatePageSize);
            }
        }

        const nextPage = Number.isFinite(page) ? page : 1;
        this.#page = this.#getValidPage(nextPage);

        this.#updatePageSizeSelect();
        this.#updateRangeText();
        this.#updateLinksDisabledState();
    }

    #updateRangeText() {
        const rangeTextElement = this.shadowRoot.querySelector('[data-range-text]');
        if (!rangeTextElement) {
            return;
        }

        const hasExplicitTotalItems = this.hasAttribute('total-items');

        if (this.totalItems === 0) {
            const ofText = this.translate('PAGINATION.OF');
            rangeTextElement.textContent = hasExplicitTotalItems ? `0-0 ${ofText} 0` : '0-0';
            return;
        }

        const currentPage = this.#getValidPage(this.#page);
        const startItemNumber = (currentPage - 1) * this.#pageSize + 1;
        const endItemNumber = Math.min(currentPage * this.#pageSize, this.totalItems);
        const ofText = this.translate('PAGINATION.OF');
        rangeTextElement.textContent = hasExplicitTotalItems
            ? `${startItemNumber}-${endItemNumber} ${ofText} ${this.totalItems}`
            : `${startItemNumber}-${endItemNumber}`;
    }

    #updateLinksDisabledState() {
        const currentPage = this.#getValidPage(this.#page);
        const totalPages = this.totalPages;

        const isFirst = currentPage <= 1;
        const isLast = totalPages === 0 ? true : currentPage >= totalPages;

        this.#setLinkDisabled(this.shadowRoot.querySelector('button[data-action="first"]'), isFirst);
        this.#setLinkDisabled(this.shadowRoot.querySelector('button[data-action="prev"]'), isFirst);
        this.#setLinkDisabled(this.shadowRoot.querySelector('button[data-action="next"]'), isLast);
        this.#setLinkDisabled(this.shadowRoot.querySelector('button[data-action="last"]'), isLast);
    }

    #setLinkDisabled(linkElement, shouldDisable) {
        if (!linkElement) {
            return;
        }
        if (shouldDisable) {
            linkElement.setAttribute('disabled', '');
            linkElement.setAttribute('aria-disabled', 'true');
        } else {
            linkElement.removeAttribute('disabled');
            linkElement.removeAttribute('aria-disabled');
        }
    }

    #updatePageSizeSelect() {
        const selectElement = this.shadowRoot.querySelector('neptune-select');
        if (!selectElement) {
            return;
        }

        const options = this.getVisiblePageSizeOptions();
        const optionValues = options.map((size) => (size === 'all' ? 'all' : String(size)));
        
        if (!optionValues.includes(this.#pageSizeValue)) {
            const shouldPreferAllForSmallTotals = this.totalItems <= 10 && optionValues.includes('all');
            let newPageSizeValue = optionValues[0];
            if (shouldPreferAllForSmallTotals) {
                newPageSizeValue = 'all';
            } else if (optionValues.includes('10')) {
                newPageSizeValue = '10';
            }
            this.#pageSizeValue = newPageSizeValue;

            if (this.#pageSizeValue === 'all') {
                this.#pageSize = Math.max(1, this.totalItems);
            } else {
                this.#pageSize = Math.floor(Number(this.#pageSizeValue));
            }
        }

        selectElement.replaceChildren();

        for (const value of options) {
            const option = document.createElement('option');
            option.value = value === 'all' ? 'all' : String(value);
            option.textContent = value === 'all' ? this.translate('PAGINATION.ALL') : String(value);
            option.selected = option.value === this.#pageSizeValue;
            selectElement.appendChild(option);
        }

        if (typeof selectElement.populateOptions === 'function') {
            selectElement.populateOptions();
            selectElement.value = this.#pageSizeValue;
        } else {
            customElements.whenDefined('neptune-select').then(() => {
                selectElement.populateOptions();
                selectElement.value = this.#pageSizeValue;
            });
        }
    }

    handleClick(event) {
        const actionLink = event.target.closest('button[data-action]');
        if (!actionLink) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        if (actionLink.hasAttribute('disabled')) {
            return;
        }

        const action = actionLink.getAttribute('data-action');
        if (!action) {
            return;
        }

        if ((action === 'first' || action === 'prev') && this.#page <= 1) {
            return;
        }

        if ((action === 'next' || action === 'last') && this.totalPages > 0 && this.#page >= this.totalPages) {
            return;
        }

        const totalPages = this.totalPages;
        let nextPage = this.#page;

        switch (action) {
            case 'first':
                nextPage = 1;
                break;
            case 'prev':
                nextPage = this.#page - 1;
                break;
            case 'next':
                nextPage = this.#page + 1;
                break;
            case 'last':
                nextPage = totalPages || 1;
                break;
            default:
                break;
        }

        nextPage = this.#getValidPage(nextPage);
        if (nextPage === this.#page) {
            return;
        }

        this.#page = nextPage;
        this.#emitChange();
    }

    handleChange(event) {
        const selectElement = event.composedPath().find(
            (element) => element instanceof HTMLElement && element.tagName?.toLowerCase() === 'neptune-select'
        );
        if (!selectElement) {
            return;
        }

        const selectedValue = String(selectElement.value || '').trim().toLowerCase();
        let normalized;

        if (selectedValue === 'all') {
            normalized = 'all';
        } else {
            const parsed = Number(selectedValue);
            if (!Number.isFinite(parsed)) {
                return;
            }

            const floored = Math.floor(parsed);
            if (floored < 1) {
                return;
            }

            normalized = String(floored);
        }

        if (this.#pageSizeValue === normalized) {
            return;
        }

        this.#pageSizeValue = normalized;
        
        if (normalized === 'all') {
            this.#pageSize = Math.max(1, this.totalItems);
        } else {
            this.#pageSize = Math.floor(Number(normalized));
        }

        this.#page = 1;
        this.#emitChange();
    }

    #emitChange() {
        this.#updateRangeText();
        this.#updateLinksDisabledState();

        const detail = {
            page: this.#page,
            pageSize: this.#pageSize,
            pageSizeValue: this.#pageSizeValue,
            totalItems: this.totalItems,
            totalPages: this.totalPages,
            offset: (this.#page - 1) * this.#pageSize,
            limit: this.#pageSize
        };

        this.dispatchEvent(new CustomEvent('pagination-change-start', {
            bubbles: true,
            composed: true,
            detail
        }));

        this.#clearQueuedPaginationChange();
        this.#changeDispatchTimeoutId = setTimeout(() => {
            this.#changeDispatchTimeoutId = null;
            this.dispatchEvent(new CustomEvent('pagination-change', {
                bubbles: true,
                composed: true,
                detail
            }));
        }, this.#changeDispatchDelayMs);
    }

    #clearQueuedPaginationChange() {
        if (this.#changeDispatchTimeoutId !== null) {
            clearTimeout(this.#changeDispatchTimeoutId);
            this.#changeDispatchTimeoutId = null;
        }
    }

    #getValidPage(pageNumber) {
        const total = this.totalPages;
        if (total === 0) {
            return 1;
        }
        return Math.min(Math.max(1, pageNumber), total);
    }
}

customElements.define('neptune-pagination', Pagination);
export default Pagination;